'use client';

import { PortableText } from '@portabletext/react';
import { useState } from 'react';
import { cn } from '@/app/utils';
import type { Template } from '../types';

interface FAQSectionProps {
  template: Template;
}

const simpleFaqComponents = {
  block: ({ children }: { children?: React.ReactNode }) => <p>{children}</p>,
  list: {
    bullet: ({ children }: { children?: React.ReactNode }) => <ul>{children}</ul>,
    number: ({ children }: { children?: React.ReactNode }) => <ol>{children}</ol>,
  },
  listItem: ({ children }: { children?: React.ReactNode }) => <li>{children}</li>,
  marks: {
    strong: ({ children }: { children?: React.ReactNode }) => <strong>{children}</strong>,
    em: ({ children }: { children?: React.ReactNode }) => <em>{children}</em>,
    link: ({ value, children }: { value?: { href: string }; children?: React.ReactNode }) => (
      <a href={value?.href} className="text-blue-400 hover:text-blue-300" target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
  },
};

export default function FAQSection({ template }: FAQSectionProps) {
  const [openItem, setOpenItem] = useState<number | null>(null);

  if (!template.faqs || template.faqs.length === 0) return null;

  const toggleItem = (index: number) => {
    if (openItem === index) {
      setOpenItem(null);
    } else {
      setOpenItem(index);
    }
  };

  return (
    <section className="w-full px-[100px] pt-20 sm:px-4 sm:pt-10">
      <div className="mx-auto max-w-[1242px]">
        <h2 className="text-center font-bold text-[40px] leading-[1.4] sm:font-semibold sm:text-[28px]">
          FAQs About {template.title}
        </h2>
        <div className="mt-[72px] sm:mt-8">
          {template.faqs.map((faq, index) => (
            <div key={`${faq._id}-${index}`} className="group border-white/10 border-b">
              <button
                onClick={() => toggleItem(index)}
                className={cn(
                  'flex w-full items-start justify-between pt-10 text-left transition-all duration-300 ease-in-out group-first-of-type:pt-0 sm:pt-6',
                  openItem === index ? 'pb-6 sm:pb-4' : 'pb-10 sm:pb-6',
                )}
              >
                <h3 className="font-semibold text-[24px] leading-[1.4] sm:text-base">{faq.question}</h3>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  className={cn(
                    'flex-shrink-0 transition-transform sm:mt-[3px] sm:h-[18px] sm:w-[18px]',
                    openItem === index ? 'rotate-180 opacity-100' : 'opacity-50',
                  )}
                >
                  <path
                    d="M4.67664 11.6716C4.21169 11.1775 4.2353 10.4003 4.72937 9.9353C5.22347 9.47035 6.00069 9.49397 6.4657 9.98804L16.2372 20.3718L26.0096 9.98804C26.4457 9.52472 27.1568 9.47532 27.6512 9.85425L27.66 9.86108L27.7382 9.92749L27.7469 9.9353C28.2408 10.4004 28.2637 11.1776 27.7987 11.6716L17.5731 22.5349L17.5673 22.5417L17.4071 22.3787L17.5663 22.5417C17.5588 22.549 17.5497 22.5578 17.538 22.5691L17.537 22.571C16.7748 23.2883 15.5746 23.2512 14.8573 22.489L4.67664 11.6716Z"
                    fill="white"
                    stroke="white"
                    strokeWidth="0.457143"
                  />
                </svg>
              </button>
              <div
                className={cn(
                  'overflow-hidden transition-all duration-300 ease-in-out',
                  openItem === index ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0',
                )}
              >
                <div className="pb-10 text-white/70 sm:text-sm">
                  <PortableText value={faq.answer} components={simpleFaqComponents} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
